import * as React from "react";
import { Dialog as SheetPrimitive } from "radix-ui";

import { cn } from "../../lib/utils";
import { Button } from "./button";
import { XIcon } from "lucide-react";
import { isSelectOverlayTarget } from "./overlay-interactions";
import { overlayScrimClassName } from "./overlay-scrim";

function Sheet({
  mobileBottomSheet: _mobileBottomSheet,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root> & {
  /** @deprecated Ignored. Sheets stay side panels at every width. */
  mobileBottomSheet?: boolean;
}) {
  void _mobileBottomSheet;
  return <SheetPrimitive.Root data-slot='sheet' {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot='sheet-trigger' {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot='sheet-close' {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return (
    <SheetPrimitive.Portal {...props}>
      <div data-slot='sheet-portal'>{props.children}</div>
    </SheetPrimitive.Portal>
  );
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot='sheet-overlay'
      className={cn(
        "data-open:animate-in data-closed:animate-out data-closed:fade-out-0 data-open:fade-in-0 fixed inset-0 z-50 duration-220 ease-out data-ending-style:opacity-0 data-starting-style:opacity-0",
        overlayScrimClassName,
        className,
      )}
      {...props}
    />
  );
}

function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  onInteractOutside,
  onPointerDownOutside,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  showCloseButton?: boolean;
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot='sheet-content'
        data-side={side}
        className={cn(
          "bg-background data-open:animate-in data-closed:animate-out data-[side=right]:data-closed:slide-out-to-right-[100%] data-[side=right]:data-open:slide-in-from-right-[100%] data-[side=left]:data-closed:slide-out-to-left-[100%] data-[side=left]:data-open:slide-in-from-left-[100%] data-[side=top]:data-closed:slide-out-to-top-[100%] data-[side=top]:data-open:slide-in-from-top-[100%] data-closed:fade-out-0 data-open:fade-in-0 data-[side=bottom]:data-closed:slide-out-to-bottom-[100%] data-[side=bottom]:data-open:slide-in-from-bottom-[100%] fixed z-50 flex flex-col gap-4 overflow-hidden border border-border bg-clip-padding text-sm shadow-[var(--shadow-dialog)] transition duration-[320ms] ease-[cubic-bezier(0.22,1,0.36,1)] sm:rounded-2xl data-[side=bottom]:inset-x-0 data-[side=bottom]:bottom-0 data-[side=bottom]:h-auto data-[side=bottom]:rounded-t-[2rem] data-[side=bottom]:border-0 data-[side=bottom]:sm:inset-x-3 data-[side=bottom]:sm:bottom-3 data-[side=bottom]:sm:rounded-2xl data-[side=left]:inset-y-0 data-[side=left]:left-0 data-[side=left]:h-full data-[side=left]:w-3/4 data-[side=left]:sm:inset-y-3 data-[side=left]:sm:left-3 data-[side=left]:sm:h-[calc(100dvh-1.5rem)] data-[side=left]:sm:max-w-sm data-[side=right]:inset-y-0 data-[side=right]:right-0 data-[side=right]:h-full data-[side=right]:w-3/4 data-[side=right]:sm:inset-y-3 data-[side=right]:sm:right-3 data-[side=right]:sm:h-[calc(100dvh-1.5rem)] data-[side=right]:sm:max-w-sm data-[side=top]:inset-x-0 data-[side=top]:top-0 data-[side=top]:h-auto data-[side=top]:sm:inset-x-3 data-[side=top]:sm:top-3 data-[side=top]:sm:rounded-2xl",
          className,
        )}
        onInteractOutside={(event) => {
          if (isSelectOverlayTarget(event.target)) {
            event.preventDefault();
            return;
          }

          onInteractOutside?.(event);
        }}
        onPointerDownOutside={(event) => {
          if (isSelectOverlayTarget(event.target)) {
            event.preventDefault();
            return;
          }

          onPointerDownOutside?.(event);
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close data-slot='sheet-close' asChild>
            <Button variant='ghost' className='absolute top-3 right-3 size-8' size='icon-sm'>
              <XIcon />
              <span className='sr-only'>Close</span>
            </Button>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot='sheet-header'
      className={cn("bg-card flex flex-col gap-0.5 border-b border-border/70 p-4", className)}
      {...props}
    />
  );
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot='sheet-footer'
      className={cn("bg-card mt-auto flex flex-row flex-wrap items-center justify-end gap-2 border-t border-border/70 p-4", className)}
      {...props}
    />
  );
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title data-slot='sheet-title' className={cn("text-foreground text-base font-medium", className)} {...props} />
  );
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot='sheet-description'
      className={cn("text-muted-foreground text-sm", className)}
      {...props}
    />
  );
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
